<script setup>
import { computed, ref, defineProps, defineEmits, watch } from 'vue'

const props = defineProps({
  errors: {
    type: Array,
    default: () => []
  },
  type: {
    type: String,
    default: 'error'
  },
  errorType: {
    type: String,
    default: 'validation'
  },
  maxVisible: {
    type: Number,
    default: 5
  },
  clearable: {
    type: Boolean,
    default: true
  }
})

defineEmits(['clear'])

const showAll = ref(false)

const ALERT_CONTENT = {
  validation: {
    title: 'Accounting CSV export blocked',
    message: 'Please fix the following issues before exporting to Xero.'
  },
  lock_failed: {
    title: 'Dockets could not be locked',
    message: 'Please review the issue before exporting again.'
  },
  no_dockets: {
    title: 'No dockets available to export',
    message: 'There are no draft dockets available for accounting export.'
  }
}

const alertContent = computed(() => {
  return (
    ALERT_CONTENT[props.errorType] || {
      title: 'Accounting export issue',
      message: 'Please review the following issue.'
    }
  )
})

const visibleErrors = computed(() => {
  if (showAll.value) return props.errors

  return props.errors.slice(0, props.maxVisible)
})

const hiddenErrorCount = computed(() => {
  return Math.max(props.errors.length - props.maxVisible, 0)
})

const hasHiddenErrors = computed(() => {
  return hiddenErrorCount.value > 0
})

function toggleShowAll() {
  showAll.value = !showAll.value
}

function getErrorMessage(error) {
  if (typeof error === 'string') return error

  return error?.message || 'Unknown validation error.'
}

watch(
  () => props.errors,
  () => {
    showAll.value = false
  }
)
</script>

<template>
  <v-alert v-if="errors.length" :type="type" outlined dense class="mb-3">
    <div class="d-flex align-start">
      <div class="flex-grow-1">
        <div class="font-weight-bold mb-1 text-start">
          {{ alertContent.title }}
        </div>

        <div class="mb-2 text-start">
          {{ alertContent.message }}
        </div>

        <ul class="mb-0 ps-4 text-start">
          <li v-for="(error, index) in visibleErrors" :key="index">
            {{ getErrorMessage(error) }}
          </li>
        </ul>

        <v-btn
          v-if="hasHiddenErrors"
          x-small
          text
          :color="type"
          class="mt-2 px-0"
          @click="toggleShowAll"
        >
          {{ showAll ? 'Show less' : `Show ${hiddenErrorCount} more` }}
        </v-btn>
      </div>

      <v-btn
        v-if="clearable"
        icon
        small
        class="ms-auto"
        @click="$emit('clear')"
      >
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>
  </v-alert>
</template>
